import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import translationService from '../../services/translationService';
import publicApi from '../../services/publicApi';

// Define available languages
export interface Language {
  id: string;
  name: string;
  nativeName: string;
  flag: string;
  direction: 'ltr' | 'rtl';
}

export const languages: Language[] = [
  {
    id: 'en-US',
    name: 'English (US)',
    nativeName: 'English (US)',
    flag: '🇺🇸',
    direction: 'ltr'
  },
  {
    id: 'en-GB',
    name: 'English (UK)',
    nativeName: 'English (UK)',
    flag: '🇬🇧',
    direction: 'ltr'
  },
  {
    id: 'es-ES',
    name: 'Spanish',
    nativeName: 'Español',
    flag: '🇪🇸',
    direction: 'ltr'
  },
  {
    id: 'fr-FR',
    name: 'French',
    nativeName: 'Français',
    flag: '🇫🇷',
    direction: 'ltr'
  },
  {
    id: 'de-DE',
    name: 'German',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    direction: 'ltr'
  },
  {
    id: 'pt-BR',
    name: 'Portuguese (Brazil)',
    nativeName: 'Português (Brasil)',
    flag: '🇧🇷',
    direction: 'ltr'
  },
  {
    id: 'it-IT',
    name: 'Italian',
    nativeName: 'Italiano',
    flag: '🇮🇹',
    direction: 'ltr'
  },
  {
    id: 'ja-JP',
    name: 'Japanese',
    nativeName: '日本語',
    flag: '🇯🇵',
    direction: 'ltr'
  },
  {
    id: 'ko-KR',
    name: 'Korean',
    nativeName: '한국어',
    flag: '🇰🇷',
    direction: 'ltr'
  },
  {
    id: 'zh-CN',
    name: 'Chinese (Simplified)',
    nativeName: '中文 (简体)',
    flag: '🇨🇳',
    direction: 'ltr'
  },
  {
    id: 'ar-SA',
    name: 'Arabic',
    nativeName: 'العربية',
    flag: '🇸🇦',
    direction: 'rtl'
  },
  {
    id: 'hi-IN',
    name: 'Hindi',
    nativeName: 'हिन्दी',
    flag: '🇮🇳',
    direction: 'ltr'
  },
  {
    id: 'mr-IN',
    name: 'Marathi',
    nativeName: 'मराठी',
    flag: '🇮🇳',
    direction: 'ltr'
  }
];

// Get default language (browser language or fallback to en-US)
const getDefaultLanguage = (): Language => {
  const browserLang = navigator.language;
  const matchingLang = languages.find(lang => lang.id === browserLang);
  return matchingLang || languages[0]; // Default to en-US
};

// Get language by ID
const getLanguageById = (id: string): Language | undefined => {
  return languages.find(lang => lang.id === id);
};

interface LanguageContextType {
  currentLanguage: Language;
  setLanguage: (languageId: string) => void;
  loadLanguageFromPreferences: (languageId: string) => void;
  refreshTranslations: () => Promise<void>;
  availableLanguages: Language[];
  t: (key: string, params?: Record<string, string | number>) => string;
  /** Use when the key may be missing: `t()` returns the key path for missing keys, so `t(k) || 'fallback'` never applies the fallback. */
  translateWithFallback: (key: string, fallback: string, params?: Record<string, string | number>) => string;
  translationsLoading: boolean;
  translations: Record<string, any>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(getDefaultLanguage());
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [translationsLoading, setTranslationsLoading] = useState(true);
  const initialTranslationLoad = useRef(true);

  // Load language from localStorage or IP detection on mount
  useEffect(() => {
    const initializeLanguage = async () => {
      // First check localStorage (user preference)
      const savedLanguageId = localStorage.getItem('tmos-language');
      if (savedLanguageId) {
        const savedLanguage = getLanguageById(savedLanguageId);
        if (savedLanguage) {
          setCurrentLanguage(savedLanguage);
          // Set document direction for RTL languages
          document.documentElement.dir = savedLanguage.direction;
          document.documentElement.lang = savedLanguage.id;
          setIsInitialized(true);
          return;
        }
      }

      // If no saved preference, try IP-based detection (only on first visit)
      const hasVisitedBefore = localStorage.getItem('tmos-has-visited');
      if (!hasVisitedBefore) {
        try {
          const response = await publicApi.get('/public/detect-language');
          
          if (response.data.success && response.data.data?.suggestedLanguage) {
            const detectedLanguage = getLanguageById(response.data.data.suggestedLanguage);
            if (detectedLanguage) {
              setCurrentLanguage(detectedLanguage);
              document.documentElement.dir = detectedLanguage.direction;
              document.documentElement.lang = detectedLanguage.id;
              // Don't save to localStorage yet - let user choose explicitly
              localStorage.setItem('tmos-has-visited', 'true');
              setIsInitialized(true);
              return;
            }
          }
        } catch (error) {
          console.warn('IP-based language detection failed, using browser default:', error);
        }
      }

      // Fallback to browser language
      const defaultLang = getDefaultLanguage();
      setCurrentLanguage(defaultLang);
      document.documentElement.dir = defaultLang.direction;
      document.documentElement.lang = defaultLang.id;
      setIsInitialized(true);
    };

    initializeLanguage();
  }, []);

  // Load translations from database
  const loadTranslations = useCallback(async (languageId: string, forceRefresh: boolean = false) => {
    setTranslationsLoading(true);
    try {
      if (forceRefresh) {
        translationService.clearCache();
      }
            const response = await translationService.getTranslationsByLanguage(languageId, undefined, undefined, forceRefresh);
      // The translation service returns the translations object directly
      console.log(`✅ Loaded ${Object.keys(response).length} translations for ${languageId}`);
      setTranslations(response as Record<string, any>);
    } catch (error) {
      console.error(`Failed to load translations from database for ${languageId}:`, error);
      // No fallback - fail clearly
      setTranslations({});
    } finally {
      setTranslationsLoading(false);
    }
  }, []);

  // Load translations when language changes; force refresh on first load so new keys are always fetched
  useEffect(() => {
    if (isInitialized) {
      const forceRefresh = initialTranslationLoad.current;
      if (forceRefresh) initialTranslationLoad.current = false;
      loadTranslations(currentLanguage.id, forceRefresh);
    }
  }, [currentLanguage.id, isInitialized, loadTranslations]);

  // Function to load language from user preferences (called from UserProfilePage)
  const loadLanguageFromPreferences = (languageId: string) => {
    const language = getLanguageById(languageId);
    if (language) {
      setCurrentLanguage(language);
      localStorage.setItem('tmos-language', languageId);
      
      // Set document direction for RTL languages
      document.documentElement.dir = language.direction;
      document.documentElement.lang = language.id;
    }
  };

  // Set language function
  const setLanguage = (languageId: string) => {
    const language = getLanguageById(languageId);
    if (language) {
      setCurrentLanguage(language);
      localStorage.setItem('tmos-language', languageId);
      
      // Set document direction for RTL languages
      document.documentElement.dir = language.direction;
      document.documentElement.lang = language.id;
    }
  };

  // Force refresh translations
  const refreshTranslations = async () => {
    await loadTranslations(currentLanguage.id, true);
  };


  // Translation function - now works with flat key-value pairs from database
  const t = (key: string, params?: Record<string, string | number>): string => {
    
    // Direct lookup in flat translations object
    const value = translations[key];
    
    
    // Missing, null, empty, or whitespace-only: treat as missing (backend usually coalesces to en-US)
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
      return key;
    }
    
    // Replace parameters if provided (supports {{param}} and {param})
    if (params) {
      return value
        .replace(/\{\{(\w+)\}\}/g, (_: string, param: string) => params[param]?.toString() ?? '')
        .replace(/\{(\w+)\}/g, (_: string, param: string) => params[param]?.toString() ?? '');
    }
    
    return value;
  };

  const translateWithFallback = useCallback(
    (key: string, fallback: string, params?: Record<string, string | number>): string => {
      const value = translations[key];
      if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
        return fallback;
      }
      if (params) {
        return value
          .replace(/\{\{(\w+)\}\}/g, (_: string, param: string) => params[param]?.toString() ?? '')
          .replace(/\{(\w+)\}/g, (_: string, param: string) => params[param]?.toString() ?? '');
      }
      return value;
    },
    [translations]
  );

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    loadLanguageFromPreferences,
    refreshTranslations,
    availableLanguages: languages,
    t,
    translateWithFallback,
    translationsLoading,
    translations,
  };

  // Don't render children until language is ready AND the first translation fetch
  // has finished. Otherwise public pages flash raw key paths (e.g. auth.forgotPassword).
  if (!isInitialized || (translationsLoading && Object.keys(translations).length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    console.error('useLanguage called outside of LanguageProvider. Check provider hierarchy.');
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

